import React, { useMemo, forwardRef, useImperativeHandle, FC, useEffect } from 'react';
import {
  ReportContainerProviders,
  ReportStoreSuspense,
} from '@sas-dvr/internal-va-react-core/components/ReportContainer';
import { ReportContentWrapper } from '@sas-dvr/internal-va-react-core/components/ReportContentWrapper';
import { ReportPageContainer } from '@sas-dvr/internal-va-react-core/components/ReportContainer/ReportPageContainer';
import { LoadingText } from '@sas-dvr/internal-va-react-core/components/ReportContainer/LoadingText';
import { useReportContext, useReportStore } from '../internal/hooks';
import { AbstractReportProps } from './SASReport';
import { createUniqueId } from '@sas-dvr/internal-va-react-core/components/utils/createUniqueId';
import { ReportHandle } from '../handles';
import { useRootClassName } from '../config';
import { BirdState } from '@sas-dvr/internal-va-react-core/store/bird/birdState';
import { Provider } from 'react-redux';
import { CoreContextProviders } from '@sas-dvr/internal-va-react-core/components/CoreContextProviders';

type IndexOrName =
  | {
      pageIndex: number;
    }
  | {
      pageName: string;
    };

export type SASReportPageProps = AbstractReportProps & {
  onReportPageLoad?: () => void;
  reportContextKey?: string;
} & IndexOrName;

/**
 * @privateRemarks
 * Typed as FC so the mobile wrappers don't have to be forward ref components.
 * Also, IndexOrName is not correctly represented in the type returned by forwardRef.
 *
 * @internal
 */
export const SASReportPage: FC<SASReportPageProps> = forwardRef<ReportHandle, SASReportPageProps>(
  function SASReportPage(props, ref) {
    const elementKey = useMemo(() => {
      return props.elementKey === undefined ? createUniqueId() : props.elementKey;
    }, [props.elementKey]);
    const pageIdentifier: string | number = 'pageName' in props ? props.pageName : props.pageIndex;

    const contextProps =
      'url' in props
        ? {
            url: props.url,
            reportUri: props.reportUri,
            authenticationType: props.authenticationType,
          }
        : {
            packageUri: props.packageUri,
          };

    const context = useReportContext({
      ...contextProps,
      objectName: pageIdentifier,
      // For performance reasons, it can be useful for report pages to share the same context.  If
      //   no reportContextKey is provided, use elementKey to get a unique context per SASReportPage.
      contextKey: props.reportContextKey ?? elementKey,
    });
    useImperativeHandle(ref, () => context.getReportHandle(), [context]);

    const store = useReportStore(context, elementKey, pageIdentifier);
    useEffect(() => {
      store.dispatch(BirdState.actions.setRestrictViewportGestures(props.restrictViewportGestures));
    }, [store, props.restrictViewportGestures]);

    const rootClassName = useRootClassName();
    return (
      <Provider store={store}>
        <CoreContextProviders popoverRootId={props.popoverRootId}>
          <ReportContentWrapper className={rootClassName}>
            <ReportContainerProviders
              url={contextProps.url}
              menuItemProvider={props.menuItemProvider}
              elementType="page"
              authenticationType={contextProps.authenticationType}
            >
              <ReportStoreSuspense
                url={contextProps.url}
                authenticationType={contextProps.authenticationType}
                fallback={() => <LoadingText />}
              >
                <ReportPageContainer onPageLoad={props.onReportPageLoad} />
              </ReportStoreSuspense>
            </ReportContainerProviders>
          </ReportContentWrapper>
        </CoreContextProviders>
      </Provider>
    );
  }
);
